import { Link, Stack, Text } from "@fluentui/react";
import { BranchRequest20Regular } from "@fluentui/react-icons";
import { styles } from "../../../Shared/shared.confirmationModel.styles";
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
} from "@fluentui/react-components";
import { REPO_URL } from "../../../Shared/shared.consts";
import useAppInsights from "../../../Hooks/UseAppInsights";
import { NavType } from "../../../Shared/AppInsights";

interface ConfirmationModelPrSectionProps {
  /**
   * The experiment configuration JSON content as a string
   */
  configJson: string;
  /**
   * The filename for the configuration JSON, used for display purposes in the PR section
   */
  configJsonFilename: string;
  /**
   * Title of the pull request
   */
  prTitle: string;
  /**
   * The URL of the pr to create or update. For create will be https://dev.azure.com/msazure/One/_git/AzureUX-Experimentation
   */
  prUrl?: string;
  /**
   * The URL of the branch to push to (if available, not provided for create)
   */
  branchUrl?: string;
  /**
   * The name of the branch to push to (if available). Will be in the format username/featureName.
   */
  branchName?: string;
}

/**
 * Component to display the PR section in the confirmation modal, including PR link, branch info, and config file content.
 */
const ConfirmationModelPrSection: React.FC<ConfirmationModelPrSectionProps> = ({
  configJson,
  configJsonFilename,
  prTitle,
  prUrl,
  branchUrl,
  branchName,
}) => {
  const { trackNavigationEvent, trackOperationEvent } = useAppInsights(
    "ConfirmationModelPrSection",
  );
  return (
    <Stack>
      <Text className={styles.confirmationModalPanelTitle}>{prTitle}</Text>
      <div className={styles.confirmationModalDetailsBox}>
        <Link
          href={prUrl ?? REPO_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.confirmationModalBranchLink}
          onClick={() => {
            trackNavigationEvent({
              destination: prUrl ?? REPO_URL,
              navType: NavType.External,
            });
          }}
        >
          {/* Branch Request Icon */}
          <BranchRequest20Regular />
          AzureUX-Experimentation
        </Link>
        <div className={styles.confirmationModalBranchInfo}>
          {branchName && (
            <span>
              {branchUrl ? (
                <a
                  href={branchUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.confirmationModalBranchLink}
                  title={branchUrl}
                  onClick={() => {
                    trackNavigationEvent({
                      destination: branchUrl,
                      navType: NavType.External,
                    });
                  }}
                >
                  {branchName}
                </a>
              ) : (
                <span className={styles.confirmationModalBranchName}>
                  {branchName}
                </span>
              )}{" "}
              into{" "}
              <span className={styles.confirmationModalBranchMain}>main</span>
            </span>
          )}
        </div>
      </div>
      <div className={styles.confirmationModalPanelTitle}>Files</div>
      <div className={styles.confirmationModalFilesBox}>
        <Accordion
          collapsible
          onClick={() => {
            trackOperationEvent({
              operation: "AccordionToggle",
              function: "Accordion.Files.onClick",
              properties: { configJsonFilename },
            });
          }}
        >
          <AccordionItem value="configFile">
            <AccordionHeader
              className={styles.confirmationModalAccordionHeader}
            >
              {configJsonFilename}
            </AccordionHeader>
            <AccordionPanel>
              <pre className={styles.confirmationModalFileContent}>
                {configJson}
              </pre>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </div>
    </Stack>
  );
};

export default ConfirmationModelPrSection;
